<template>
  <div
    class="tag-input"
    tabindex="0"
    @click.stop="activeIndexToDelete = null"
    ref="tagInputContainer"
    v-click-outside="handleClickOutside"
    @focus="inputFocused = true"
  >
    <div
      class="list-teams"
      :class="{
        '--focused': inputFocused
      }"
      v-if="teams.length > 0 && !onTyping && !hideTeam"
    >
      <div
        class="team"
        @click.stop="addTeamTag(team)"
        v-for="(team, index) in teams"
        :key="index"
      >
        <div>
          <aha-team-avatar
            :name="team.name"
            :id="team.id"
            :width="35"
            :height="35"
            class="text-sm"
            :circle="true"
          />
        </div>
        <div>
          <div class="name">
            {{ team.name }}
          </div>
          <div class="flex gap-1">
            <aha-icon
              name="users"
              width="18"
              height="18"
              viewBox="0 0 14 14"
            />
            <span class="text-xs text-base-80">{{ team.numberOfMembers }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      class="tag-input__frame"
      :class="{
        '--focused': inputFocused,
        '--disabled': disabled,
        '--empty': !tags.length,
      }"
    >
      <div class="flex flex-wrap w-full items-center tag-input__frame__input">
        <div
          v-for="(tag, index) in tags"
          class="tag-input__tag"
          :class="{'--deleting': index === activeIndexToDelete, '--invalid': !isValid(tag) } "
          @click="activeIndexToDelete = index"
          :key="index"
          tabindex="0"
        >
          <span class="flex-1 break-all">{{ tag.name }}</span>
          <span @click="removeTag(index)">
            <aha-icon
              name="close"
              width="10"
              height="10"
              viewBox="0 0 24 24"
            />
          </span>
        </div>
        <!-- 186 = semi-colon -->
        <!-- 188 = comma -->
        <input
          type="text"
          :placeholder="placeholder"
          class="tag-input__input"
          :disabled="disabled"
          @focus="handleFocusInput"
          @blur="handleInputBlur"
          @keydown="onKeyDownHandler"
          @keydown.delete="removeLastTag"
          @keydown.tab="addTag"
          @keydown.enter="addTag"
          @keyup="onKeyUpHandler"
          :spellcheck="spellcheck"
          :autocomplete="autocomplete"
        >
      </div>
      <slot name="roleselection" />
    </div>
  </div>
</template>
<script>
import Validate from '../../../utils/validate-email'

import AhaIcon from '../Icon.vue'

// inspiration: https://learnvue.co/2020/01/build-a-custom-vuejs-tag-input-in-under-10-minutes/
export default {
  components: {
    AhaIcon,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    validator: {
      type: [String, Function],
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Enter a Tag',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    formatter: {
      type: Function,
      default: null,
    },
    allowDuplicate: {
      type: Boolean,
      default: true,
    },
    spellcheck: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    tagType: {
      type: String,
      default: '',
    },
    teams: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      tags: [],
      activeIndexToDelete: null,
      inputFocused: true,
      onTyping: false,
      hideTeam: false,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.tags = newValue
      },
    },
  },
  mounted() {
    this.regex = new RegExp(this.validator)
  },
  methods: {
    addTag(event) {
      const val = event.target.value.trim().replace(',', '')
      if (val === ',') return
      if (val.length > 0 && val !== ',') {
        const entries = this.parseEntries(val)
        if (entries.length) {
          entries.forEach((entry) => {
            let entryToAdd = entry
            if (this.formatter && typeof this.formatter === 'function') {
              entryToAdd = this.formatter(entryToAdd)
            }
            if (!this.allowDuplicate) {
              if (this.tags.indexOf(entryToAdd) > -1) return
            }
            if (entryToAdd.length) {
              if (!this.tagType) {
                this.tags.push({ name: entryToAdd })
              } else if (this.tagType === 'email' && Validate.validateEmail(entryToAdd)) {
                this.tags.push({ name: entryToAdd })
              }
            }
          })
          this.$emit('input', this.tags)
          event.target.value = ''
          this.$emit('tagExists', false)
        }
      }
    },

    handleFocusInput() {
      this.inputFocused = true
      const container = this.$refs.tagInputContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    // eslint-disable-next-line max-len
    // input: 1@a.com,2@a.com ,3@a.com, 4@a.com; 5@a.com;6@a.com ;7@x.com 8@x.com     9@x.com ,      10@x.com ee,ae
    // eslint-disable-next-line max-len
    // output: [1@a.com, 2@a.com, 3@a.com, 4@a.com, 5@a.com, 6@a.com, 7@x.com, 8@x.com, 9@x.com, 10@x.com, ee, ae]
    parseEntries(string) {
      // look for: , and ; and space character
      const regex = /(\s*,\s*|\s*;\s*|\s+)/ig
      const entries = string.replace(regex, ',')
      // split by ,
      return entries.split(',')
    },

    removeTag(index) {
      if (this.tags[index] && this.tags[index].id) {
        this.hideTeam = false
      }
      this.tags.splice(index, 1)
      if (this.tags.length === 0) {
        this.onTyping = false
      }
      this.$emit('input', this.tags)
    },
    clearAllTags() {
      this.tags = []
    },
    onKeyDownHandler(event) {
      // 188: ,
      // 59: ;
      // 32: space
      this.onTyping = true
      if (event.keyCode === 188 || event.keyCode === 59 || event.keyCode === 32) {
        event.preventDefault()
        this.addTag(event)
      }
    },
    onKeyUpHandler(event) {
      if (event.target.value.trim() === '') {
        this.onTyping = false
      }
    },
    removeLastTag(event) {
      if (event.target.value.trim() === '') {
        this.onTyping = false
      }
      if (!Number.isInteger(this.activeIndexToDelete) && event.target.value.trim() === '') {
        this.activeIndexToDelete = this.tags.length - 1
        return
      }
      if (this.activeIndexToDelete === null) {
        return
      }
      this.removeTag(this.activeIndexToDelete)
      this.activeIndexToDelete = null
    },
    isValid(tag) {
      if (tag.id) return true
      if (!this.validator) return true
      if (typeof this.validator === 'string') { // regex
        return this.regex.test(tag.name)
      }
      if (typeof this.validator === 'function') { // validator function
        return this.validator(tag.name)
      }
      return false
    },
    handleInputBlur(event) {
      // this.inputFocused = false
      this.addTag(event)
    },
    handleClickOutside() {
      this.onTyping = false
      this.inputFocused = false
    },
    addTeamTag(team) {
      const tagExists = this.tags.filter((tag) => tag.id && tag.id === team.id)
      if (tagExists.length === 0) {
        this.hideTeam = true
        this.tags.push(team)
      }
    },
  },
}

</script>
<style lang="scss" scoped>
.tag-input {
  min-height: 40px;
  @apply w-full relative;
  &__tag {
    @apply text-base-90 flex bg-base-0 justify-center items-center;
    @apply border border-base-60 rounded m-1 text-md14-h18 py-1 px-2;
    &:focus {
      background: #348EF6 !important;
      color: #fff !important;
      border: 1px solid transparent !important;
    }
    &.--invalid {
      @apply border border-red-50 text-red-50;
    }
    &.--deleting {
      @apply bg-red-50 text-base-0 border border-transparent;
    }
  }
  &__tag > span {
    @apply cursor-pointer ml-1;
  }
  &__input {
    width: 5px;
    height: 28px;
    @apply text-md14-h18 py-1 px-2 flex-1 border-none outline-none;
    @apply appearance-none placeholder-base-70 bg-transparent;
    &:focus {
      width: 90px;
    }
  }
  > .list-teams {
    @apply absolute bg-white-100 w-full z-10 hidden;
    padding: 4px;
    border-radius: 4px;
    top: calc(100% + 8px);
    border: 1px solid #D1D1D1;
    > .team {
      gap: 6px;
      display: flex;
      padding: 10px;
      cursor: pointer;
      &:hover {
        background-color: #F5F5F5;
      }
      .name {
        font-weight: bold;
      }
    }
    &.--focused {
      display: block;
    }
  }
}
.tag-input__frame {
  @apply overflow-y-auto border border-secondary-20 rounded flex max-h-24;
  @apply hover:border-primary-90;

  &.--focused {
    @apply border-primary-90 outline-none;
  }
  &.--disabled {
    @apply border-secondary-20 outline-none;
  }
}
.tag-input__frame__input {
  width: calc(100% - 75px);
}
</style>
